import { Locale, useSiteContext } from '@zooplus/commons-lang';

/**
 * Formats a date according to the specified locale and formatting options.
 *
 * @param locale - The locale string (e.g. 'es-ES') used for formatting the date.
 * @param date - The date to format. Can be a string, number, or Date object.
 * @param options - Optional. An object specifying formatting options for the date (defaults to year, month, and day in 2-digit format).
 * @returns The formatted date string according to the locale and options.
 */
export function formatDate(
  locale: string,
  date: string | number | Date,
  options: Intl.DateTimeFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit' },
) {
  return new Date(date).toLocaleDateString(locale, options);
}

/**
 * This hook returns the date formatting function bound to the current locale from `SiteContext`.
 * @returns A function that formats a date according to the current locale from `SiteContext`.
 */
export function useDateFormatter() {
  const { locale } = useSiteContext();

  return formatDate.bind(null, locale);
}

/**
 * Checks if a given date falls within the current quarter of the year.
 *
 * @param date - The date to check. Can be a Date object, a string, or a number (timestamp).
 * @returns True if the date is in the current quarter, false otherwise.
 */
export function isInThisQuarter(date: Date | string | number): boolean {
  const now = new Date();
  const targetDate = new Date(date);

  const currentQuarter = Math.floor(now.getMonth() / 3);
  const dateQuarter = Math.floor(targetDate.getMonth() / 3);

  return now.getFullYear() === targetDate.getFullYear() && currentQuarter === dateQuarter;
}

/**
 * Returns the end date of the current quarter.
 * Useful for testing purposes (in tests or storybook stories).
 * @returns A Date object representing the last day of the current quarter.
 */
export function getEndOfCurrentQuarter() {
  const now = new Date();
  const quarter = Math.floor(now.getMonth() / 3);
  const endMonth = (quarter + 1) * 3 - 1;
  const endDate = new Date(now.getFullYear(), endMonth + 1, 0); // 0th day of next month = last day of this month
  return endDate;
}

/**
 * Returns a date that falls outside the current quarter.
 * Useful for testing purposes (in tests or storybook stories).
 * @returns A Date object representing a date outside the current quarter.
 */
export function getDateOutsideCurrentQuarter() {
  const now = new Date();
  const quarter = Math.floor(now.getMonth() / 3);
  // Move to next quarter
  let nextQuarter = quarter + 1;
  let year = now.getFullYear();
  if (nextQuarter > 3) {
    nextQuarter = 0;
    year += 1;
  }
  const startMonth = nextQuarter * 3;
  // Pick the 15th day of the first month of the next quarter
  const date = new Date(year, startMonth, 15);
  return date;
}

function toLocalMidnight(value: Date | string): Date {
  return typeof value === 'string'
    ? parseLocalDate(value)
    : new Date(value.getFullYear(), value.getMonth(), value.getDate());
}

/**
 * Returns the whole-day difference between two dates (start - end), day precision.
 * Both arguments are normalized to local midnight before diffing, so a 'YYYY-MM-DD' string
 * and a Date representing "now" compare correctly regardless of the runtime's timezone.
 */
export function getDaysDifference(start: Date | string, end: Date | string): number {
  const dayMilliseconds = 1000 * 60 * 60 * 24;
  return Math.round(
    (toLocalMidnight(start).getTime() - toLocalMidnight(end).getTime()) / dayMilliseconds,
  );
}

/**
 * Formats the difference between two dates in a human-readable relative format (e.g., "in 3 days", "3 days ago",
 * "tomorrow", "yesterday") according to the specified locale.
 * @returns A string representing the relative time difference between the two dates.
 * **/
export function formatRelative(locale: Locale, start: Date | string, end: Date | string) {
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  return rtf.format(getDaysDifference(start, end), 'day');
}

/**
 * Returns a Date representing local midnight of the current day ("today", start-of-day).
 */
export function getStartOfToday(): Date {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return now;
}

export function useDateRelativeFormatter() {
  const { locale } = useSiteContext();

  return formatRelative.bind(null, locale);
}

/**
 * Formats a Date to YYYY-MM-DD string (day precision, like Java's LocalDate)
 */
export function formatDateToLocalDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Parses a 'YYYY-MM-DD' calendar-date string (day precision, like Java's LocalDate) into a Date
 * representing local midnight of that calendar day — the inverse of formatDateToLocalDate.
 * Unlike `new Date('YYYY-MM-DD')`, which the JS spec parses as UTC midnight, this always yields
 * a Date whose wall-clock local date matches the input string, regardless of the runtime's timezone.
 * Returns an Invalid Date if the input isn't in 'YYYY-MM-DD' shape.
 */
export function parseLocalDate(dateOnly: string): Date {
  const match = DATE_ONLY_PATTERN.exec(dateOnly);
  if (!match) return new Date(NaN);
  const [, year, month, day] = match;
  return new Date(Number(year), Number(month) - 1, Number(day));
}

/**
 * Creates a date offset by months and returns it as YYYY-MM-DD string
 */
export function getLocalDateByMonthOffset(monthOffset: number): string {
  const d = new Date();
  d.setMonth(d.getMonth() + monthOffset);
  return formatDateToLocalDate(d);
}

/**
 * Creates a date offset by days and returns it as YYYY-MM-DD string
 */
export function getLocalDateByDayOffset(dayOffset: number): string {
  const d = new Date();
  d.setDate(d.getDate() + dayOffset);
  return formatDateToLocalDate(d);
}
